import { LiveAnnouncer } from '@angular/cdk/a11y';
import { AfterViewInit, Component, forwardRef, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { IUser } from '@portal/entity-services/interfaces';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { AuthenticationService } from '@portal/shared/auth/authentication/src';
import { UserService } from '@portal/shared/user/src';
import { finalize } from 'rxjs/operators';
import { UserFullNameService } from '../../services/user-full-name.service';
import { FormUserComponent } from '../form/form.component';
import { ErrorMessage } from '../../enums/error-message.enum';
import { NotifierService } from '@portal/shared/ui/notifier/src';
import { HttpError } from '@portal/shared/error-handler/src';
import { HttpErrorResponse } from '@angular/common/http';

declare const $localize;

@Component({
  selector: 'portal-create-user',
  templateUrl: './create.component.html',
  standalone: false,
})
export class CreateUserComponent implements OnInit, AfterViewInit {
  @ViewChild(forwardRef(() => FormUserComponent)) formUserComponent: FormUserComponent;
  saving = false;
  loading = false;
  loggedInUser: IUser;

  constructor(
    private organisationService: OrganisationService,
    private router: Router,
    private title: Title,
    private liveAnnouncer: LiveAnnouncer,
    private userFullNameService: UserFullNameService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.title.setTitle($localize`:Page title:Add new user`);
    this.liveAnnouncer.announce($localize`Navigated to ${this.title.getTitle()}`);
    const loggedInUserId = this.authenticationService.userUid;
    this.loading = true;
    this.userService.getByKey(loggedInUserId).subscribe((user: IUser) => {
      this.loggedInUser = user;
      this.loading = false;
    });
  }

  ngAfterViewInit(): void {
    this.setFocus();
  }

  setFocus(): void {
    if (this.formUserComponent) {
      if (this.formUserComponent.firstNameInput) {
        this.formUserComponent.firstNameInput?.nativeElement?.focus();
      } else if (this.formUserComponent.fullNameInput) {
        this.formUserComponent.fullNameInput?.nativeElement?.focus();
      }
    } else {
      setTimeout(() => {
        this.setFocus();
      }, 1000);
    }
  }

  onFormValidated(user: IUser): void {
    const fullName = this.userFullNameService.getFullUserName(user);
    user.nameDetails.alternateFullName = user.name = fullName;

    this.saving = true;

    this.organisationService
      .addUser(user, user.entityUid)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (addedUser: IUser) => {
          this.notifier.toast(
            $localize`${addedUser.name} (${addedUser.userUid}) is successfully created`,
          );
          this.router.navigate([`/administration/users/${addedUser.userUid}`]);
        },
        error: (error: HttpErrorResponse) => {
          this.handleEmailDomainError(error);
        },
      });
  }

  private handleEmailDomainError(error: HttpErrorResponse): void {
    const errorStatus = error?.status || error?.error?.status;
    const emailErrorMessage =
      error?.error?.additionalProperties?.error || error?.error?.error?.additionalProperties?.error;

    if (
      errorStatus === HttpError.CodeError400 &&
      emailErrorMessage === ErrorMessage.EMAIL_DOMAIN_NOT_SUPPORTED
    ) {
      this.notifier.error(
        $localize`This email domain is not supported. Please try another address.`,
      );
    }
  }
}
